import { ButtonBase, Typography } from '@material-ui/core';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import { Color, ColorDynamic, Inline, Stack } from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { useMemo, useRef, useState } from 'react';
import defaultImage from 'shared/icons/assets/black_default_image.png';
import { ErrorIcon } from 'shared/icons/ErrorIcon';
import { PlayIcon } from 'shared/icons/PlayIcon';
import { formatDuration, useInlineMedia } from 'shared/utils/MediaUtils';
import styled from 'styled-components';
import { PhotoDTO, VideoDTO } from '../../carrier/loads/data/LoadDTO';
import { PhotoViewer } from './PhotoViewer';
import { PhotoViewMode } from './PhotoViewMode';

const StyledPlayIcon = styled(PlayIcon)`
  width: 20px;
  height: 18px;
`;

const PhotoGalleryWrapper = styled.div<{ isVideo: boolean }>`
  display: flex;
  align-items: stretch;
  & button {
    width: 50px;
    text-align: center;
    transition:
      opacity 300ms,
      background 300ms;
    border-radius: 3px;
    flex-shrink: 0;
  }
  & button:hover {
    background: ${ColorDynamic.Silver400};
  }
  & > *:nth-child(2) {
    flex-grow: 1;
    margin: auto 16px;
    display: flex;
    justify-content: center;
    height: 480px;
  }
  & img {
    object-fit: ${({ isVideo }) => (isVideo ? 'fill' : 'contain')};
    user-select: none;
  }
`;

const ThunmbnailsContainer = styled.div`
  white-space: nowrap;
  overflow-y: hidden;
  overflow-x: auto;
  position: relative;
  display: flex;
  justify-content: start;

  & div {
    margin: auto;
    display: flex;
    justify-content: center;
  }
`;

const ThumbnailItem = styled(ButtonBase)`
  margin-left: 8px;
  opacity: 0.5;
  &[data-selected='true'] {
    border: 2px solid ${Color.Blue300};
    opacity: 1;
  }

  &[data-non-vehicle='true'] {
    opacity: 1;
  }

  & img {
    object-fit: cover;
  }
`;

const NonVehiclePhotoBagde = styled.div`
  position: absolute;
  z-index: 2;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
`;

const VideoContainer = styled.div`
  position: absolute;
  z-index: 2;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const VideoDuration = styled.div`
  z-index: 2;
  position: absolute;
  bottom: 4px;
  right: 4px;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 2px;
  padding: 0 2px;
  color: ${Color.White};
  font-size: 11px;
  font-weight: 400;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ButtonContainer = styled(Box)<{ width?: string; height?: string }>`
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  width: ${({ width = '80px' }) => width};
  height: ${({ height = '80px' }) => height};
  display: flex;
  justify-content: center;
  align-items: center;
`;

export interface PhotoGalleryProps {
  photos: PhotoDTO[];
  videos?: VideoDTO[];
  onView?: (type: 'image' | 'video' | undefined) => void;
}

export function InlinePhotoGallery({
  photos,
  videos,
  onView,
}: PhotoGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [shouldShowViewMode, setShowViewMode] = useState(false);
  const thumbnailRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const inlinePhotos = useInlineMedia(photos, videos);

  const damages = useMemo(
    () => photos[selectedIndex]?.damages || [],
    [selectedIndex, photos],
  );

  const handleScrollThumbnailIntoView = () => {
    thumbnailRefs.current[selectedIndex]?.scrollIntoView({
      inline: 'center',
      block: 'nearest',
      behavior: 'smooth',
    });
  };

  const hasPrevious = selectedIndex !== 0;
  const hasNext = selectedIndex !== inlinePhotos.length - 1;
  const isVideo = Boolean(inlinePhotos[selectedIndex]?.is_video);

  return (
    <Stack space="small">
      {damages.length > 0 && (
        <Box paddingBottom="small">
          <Stack space="xxsmall">
            {damages.map((damage) => (
              <Inline space="xxsmall" key={damage.code}>
                <Typography variant="body1">{damage.code}</Typography>
                {damage.note && (
                  <Typography
                    key={damage.code}
                    color="textSecondary"
                    variant="body2"
                  >
                    - {damage.note}
                  </Typography>
                )}
              </Inline>
            ))}
          </Stack>
        </Box>
      )}
      <PhotoGalleryWrapper isVideo={isVideo}>
        <ButtonBase
          aria-label="go to previous"
          disabled={!hasPrevious}
          onClick={() => {
            handleScrollThumbnailIntoView();
            setSelectedIndex(selectedIndex - 1);
          }}
        >
          <ChevronLeft color={!hasPrevious ? 'disabled' : 'inherit'} />
        </ButtonBase>
        <Box position="relative">
          <PhotoViewer
            videos={videos}
            photos={photos}
            inlinePhotos={inlinePhotos}
            total={inlinePhotos.length}
            selectedIndex={selectedIndex}
            key={selectedIndex}
            url={inlinePhotos[selectedIndex]?.url || ''}
            isVideo={inlinePhotos[selectedIndex]?.is_video}
            onClose={(index) => {
              setSelectedIndex(index);
            }}
            onView={onView}
            onModeChange={() => {
              setShowViewMode(true);
            }}
          />
        </Box>
        <ButtonBase
          aria-label="go to next"
          disabled={!hasNext}
          onClick={() => {
            handleScrollThumbnailIntoView();
            setSelectedIndex(selectedIndex + 1);
          }}
        >
          <ChevronRight color={!hasNext ? 'disabled' : 'inherit'} />
        </ButtonBase>
      </PhotoGalleryWrapper>
      <ThunmbnailsContainer>
        <div>
          {inlinePhotos.map(
            (
              {
                id,
                thumbnail_url,
                is_vehicle_detected,
                is_video,
                duration_in_seconds,
              },
              idx,
            ) => (
              <ThumbnailItem
                key={id}
                data-selected={idx === selectedIndex}
                data-non-vehicle={is_vehicle_detected === false}
                ref={(node) => {
                  thumbnailRefs.current[idx] = node;
                }}
                onClick={(event) => {
                  event.currentTarget.scrollIntoView({
                    inline: 'center',
                    block: 'nearest',
                    behavior: 'smooth',
                  });
                  setSelectedIndex(idx);
                }}
              >
                <img
                  alt={`gallery thumbnail image ${idx + 1}`}
                  src={thumbnail_url}
                  height="100px"
                  width="100px"
                  onError={(e) => {
                    e.currentTarget.src = defaultImage;
                  }}
                />
                {is_video && (
                  <>
                    <VideoContainer>
                      <ButtonContainer width="38px" height="38px">
                        <StyledPlayIcon />
                      </ButtonContainer>
                    </VideoContainer>
                    {duration_in_seconds ? (
                      <VideoDuration>
                        {formatDuration(duration_in_seconds)}
                      </VideoDuration>
                    ) : null}
                  </>
                )}
                {is_vehicle_detected === false && (
                  <NonVehiclePhotoBagde aria-label="Non-vehicle photo badge">
                    <ErrorIcon />
                  </NonVehiclePhotoBagde>
                )}
              </ThumbnailItem>
            ),
          )}
        </div>
      </ThunmbnailsContainer>

      <PhotoViewMode
        open={shouldShowViewMode}
        inlinePhotos={inlinePhotos}
        onClose={() => {
          setShowViewMode(false);
        }}
      />
    </Stack>
  );
}
